/**
 * UAT-B2 (C1 CD export, frames 1b INVITE / 1c SENT) — the 13–15 guardian-invite
 * beat. An UNNUMBERED OnboardingShell `metaOverride` screen mounted after the
 * wizard's last question, before Step7Welcome, for a fresh 13–15 athlete with
 * zero guardian relationships. NEVER blocking: every screen has a same-weight
 * path past it (Skip / Continue / Sign out — the shell owns Back + Sign out).
 *
 * The shell owns the ambient panel + top row (Back · "Optional" · Sign out) via
 * metaOverride; this component is the step BODY only.
 *
 * EC-0: no body/health data — the guardian pitch is derived-signals-only, the
 * "stays private" list is deliberately longer than "can see", and the word
 * "safe" never renders. Copy is VERBATIM from the ratified export (em dashes
 * and curly apostrophes included). Invite flows through the athlete-side
 * `useRelationships().inviteGuardian` (the same authorized endpoint the adult
 * form uses); the sent state shows the verbatim anti-enumeration copy.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useRelationships } from '../../hooks/useRelationships';
import { INVITE_RECORDED_COPY } from '../guardian/GuardianInvitePanel';
import { StepCta } from '../OnboardingShell/StepLayout';
import { useOnboardingV2 } from '../OnboardingShell/onboardingV2';
import styles from './GuardianInviteBeat.module.css';

type GuardianInviteBeatProps = {
  /** Server-derived age (13–15) — slots into the lede. */
  age: number;
  /** Skip-for-now: stamps `_guardian_invite_skipped_at`, then → Welcome. */
  onSkip: () => void;
  /** Continue after a sent invite → Welcome. */
  onContinue: () => void;
};

const CHECK = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.5 8.5 7 12l6-7.5" />
  </svg>
);

const LOCK = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3.5" y="7" width="9" height="6" rx="1.5" />
    <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
  </svg>
);

/** EC-0 — the "stays private" list is deliberately longer than "can see";
 *  every row is labelled + lock-glyph'd (colour is never the only cue). */
const STAYS_PRIVATE_ROWS = [
  'Your weight & body measurements',
  'Your health & screening answers',
  'Your chats with your coach',
] as const;

export function GuardianInviteBeat({ age, onSkip, onContinue }: GuardianInviteBeatProps) {
  // Part 7 (flag `onboarding_2_0_v1`): after the invite is sent, Continue is the screen's one
  // primary button and docks like every other step's.
  const v2 = useOnboardingV2();
  const { inviteGuardian, relationships, revoke } = useRelationships();
  const [email, setEmail] = useState('');
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSend = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await inviteGuardian(trimmed);
      setSentEmail(trimmed);
      setEmail('');
    } catch (err) {
      // CG-7 errorNote grammar — CTA re-enabled for retry.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // The just-created pending row (athlete-side guardian invite this athlete
  // sent) — used only to power "Withdraw". Anti-enumeration: the counterpart
  // echoes only the typed email pre-consent, so we key on the typed value.
  const pendingRow = relationships.find(
    (r) => r.mySide === 'athlete' && r.role === 'guardian' && r.status === 'invited' && r.iInvited,
  );

  const onWithdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      if (pendingRow) await revoke(pendingRow.id);
      setSentEmail(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // ── SENT state (frame 1c) ──────────────────────────────────────────────
  if (sentEmail) {
    return (
      <div className={styles.body}>
        <p className={styles.notice} role="status">
          {INVITE_RECORDED_COPY}
        </p>

        <div className={styles.pendingRow}>
          <span className={styles.pendingEmail}>{sentEmail}</span>
          <span className={styles.pendingStatus}>Waiting for them to accept</span>
          <button
            type="button"
            className={styles.withdrawBtn}
            disabled={busy}
            onClick={() => void onWithdraw()}
          >
            Withdraw
          </button>
        </div>

        {v2 ? (
          <StepCta onClick={onContinue}>Continue</StepCta>
        ) : (
          <button type="button" className={styles.primaryPill} onClick={onContinue}>
            Continue
          </button>
        )}
        <p className={styles.subNote}>
          You&rsquo;re done here. They confirm from their side whenever
          they&rsquo;re ready.
        </p>

        {error && (
          <p className={styles.errorNote} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  // ── INVITE state (frame 1b) ────────────────────────────────────────────
  return (
    <div className={styles.body}>
      <p className={styles.lede}>
        You&rsquo;re {age}, so we recommend adding a parent or guardian. Their
        whole job: OK the occasional plan change. Training works the same either
        way.
      </p>

      <div className={styles.lists}>
        <div className={styles.canSee}>
          <div className={styles.canSeeKicker}>They can see</div>
          <div className={styles.rowList}>
            <div className={styles.seeRow}>
              <span className={styles.okIcon}>{CHECK}</span>
              <span>Whether your training&rsquo;s on track this week</span>
            </div>
            <div className={styles.seeRow}>
              <span className={styles.okIcon}>{CHECK}</span>
              <span>Plan changes that need their OK</span>
            </div>
          </div>
        </div>

        <div className={styles.staysPrivate}>
          <div className={styles.privateKicker}>
            <span className={styles.privateLock}>{LOCK}</span>
            Stays private: they can&rsquo;t see any of this
          </div>
          <div className={styles.rowList}>
            {STAYS_PRIVATE_ROWS.map((row) => (
              <div key={row} className={styles.privateRow}>
                <span className={styles.lockIcon}>{LOCK}</span>
                <span>{row}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <form className={styles.form} onSubmit={(e) => void onSend(e)}>
        <label className={styles.fieldLabel} htmlFor="guardian-beat-email">
          Their email
        </label>
        <input
          id="guardian-beat-email"
          type="email"
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Their email"
        />
        <button
          type="submit"
          className={styles.primaryPill}
          disabled={busy || !email.trim()}
        >
          Send invite
        </button>
      </form>
      <p className={styles.note}>
        Nothing is shared until you both confirm. No account yet? The invite
        connects when they sign up.
      </p>

      <div className={styles.skipWrap}>
        <button type="button" className={styles.skipBtn} onClick={onSkip}>
          Skip for now
        </button>
        <p className={styles.skipHelper}>You can do this anytime in Settings.</p>
      </div>

      {error && (
        <p className={styles.errorNote} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
